// Date helpers for feed rows and day headers. `now` is a parameter so tests
// can pin the clock.

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const parse = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const calendarDaysBetween = (earlier: Date, later: Date): number =>
  Math.round((startOfDay(later) - startOfDay(earlier)) / DAY);

// "just now", "5m ago", "2h ago", "Yesterday", "Aug 31", "Aug 31, 2025".
export const formatRelativeTime = (
  iso: string | null | undefined,
  now: Date = new Date()
): string => {
  const date = parse(iso);
  if (!date) return '';

  const elapsed = now.getTime() - date.getTime();
  if (elapsed < MINUTE) return 'just now';

  const days = calendarDaysBetween(date, now);
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m ago`;
  if (days === 0) return `${Math.floor(elapsed / HOUR)}h ago`;
  if (days === 1) return 'Yesterday';

  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' })
  });
};

// Full timestamp for the title attribute, e.g. "Aug 31, 2026, 3:05 PM".
export const formatFullTimestamp = (iso: string | null | undefined): string => {
  const date = parse(iso);
  if (!date) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

// Day group header: "Today", "Yesterday", or "Aug 31, 2026".
export const dayLabel = (iso: string | null | undefined, now: Date = new Date()): string => {
  const date = parse(iso);
  if (!date) return 'Earlier';
  const days = calendarDaysBetween(date, now);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
